import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, StarOff, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";

const BLANK = {
  title: "", summary: "", description: "", tech_stack: [], cover_image: "", live_url: "", repo_url: "",
  featured: false, order: 0, year: new Date().getFullYear(),
  category: "", company: "", role: "", team_size: "", period: "", metrics: [], case_study_html: "",
};

// Metrics are edited one per line as "value | label | fill 0-1", e.g. "40% | Lower p99 | 0.8".
const metricsToText = (ms) => (ms || []).map((m) => `${m.value} | ${m.label} | ${m.pct}`).join("\n");
const textToMetrics = (t) =>
  t.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter(([v, l]) => v && l)
    .map(([value, label, pct]) => ({ value, label, pct: Math.min(1, Math.max(0, Number(pct) || 0.75)) }));

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [metricsText, setMetricsText] = useState("");

  const load = () => api.get("/projects").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(BLANK); setMetricsText(""); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setDraft({ ...BLANK, ...p, tech_stack: p.tech_stack || [] });
    setMetricsText(metricsToText(p.metrics));
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const body = { ...draft, metrics: textToMetrics(metricsText) };
      if (editingId) await api.put(`/projects/${editingId}`, body);
      else await api.post("/projects", body);
      setOpen(false);
      toast.success("Saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.put(`/projects/${p.id}`, { featured: !p.featured });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Projects</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Portfolio</h1>
        </div>
        <Button onClick={openNew} data-testid="project-new"><Plus className="h-4 w-4" /> New project</Button>
      </header>

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left label-xs">
              <th className="p-3">Title</th>
              <th className="p-3">Year</th>
              <th className="p-3">Tech</th>
              <th className="p-3 w-24">Featured</th>
              <th className="p-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="p-3 font-medium cursor-pointer" onClick={() => openEdit(p)} data-testid={`project-open-${p.id}`}>{p.title}</td>
                <td className="p-3 text-muted-foreground font-mono text-xs">{p.year}</td>
                <td className="p-3 text-muted-foreground text-xs">{p.tech_stack?.slice(0, 4).join(", ")}</td>
                <td className="p-3">
                  <Button variant="ghost" size="icon" onClick={() => toggleFeatured(p)}>
                    {p.featured ? <Star className="h-4 w-4 text-[var(--brand-accent)]" /> : <StarOff className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto">
            <FF label="Title" v={draft.title} on={(v) => setDraft({ ...draft, title: v })} tid="project-title" />
            <FT label="Summary" v={draft.summary} on={(v) => setDraft({ ...draft, summary: v })} rows={2} />
            <FT label="Description" v={draft.description} on={(v) => setDraft({ ...draft, description: v })} rows={4} />
            <div>
              <Label className="label-xs">Tech stack (comma-separated)</Label>
              <Input className="mt-2" value={draft.tech_stack.join(", ")} onChange={(e) => setDraft({ ...draft, tech_stack: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <FF label="Cover image URL" v={draft.cover_image} on={(v) => setDraft({ ...draft, cover_image: v })} />
            <div className="grid grid-cols-2 gap-3">
              <FF label="Live URL" v={draft.live_url} on={(v) => setDraft({ ...draft, live_url: v })} />
              <FF label="Repo URL" v={draft.repo_url} on={(v) => setDraft({ ...draft, repo_url: v })} />
              <FF label="Year" type="number" v={draft.year} on={(v) => setDraft({ ...draft, year: Number(v) })} />
              <FF label="Order" type="number" v={draft.order} on={(v) => setDraft({ ...draft, order: Number(v) })} />
            </div>
            <p className="label-xs pt-2">Case study page</p>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Category (projects filter)" v={draft.category} on={(v) => setDraft({ ...draft, category: v })} />
              <FF label="Company" v={draft.company} on={(v) => setDraft({ ...draft, company: v })} />
              <FF label="Your role" v={draft.role} on={(v) => setDraft({ ...draft, role: v })} />
              <FF label="Team size" v={draft.team_size} on={(v) => setDraft({ ...draft, team_size: v })} />
              <FF label="Timeframe (e.g. Mar 2023 to Jan 2024)" v={draft.period} on={(v) => setDraft({ ...draft, period: v })} />
            </div>
            <FT label="Metrics, one per line: value | label | fill 0-1" v={metricsText} on={setMetricsText} rows={4} />
            <div>
              <Label className="label-xs">Case study (each H2 becomes a section)</Label>
              <div className="mt-2">
                <RichTextEditor value={draft.case_study_html} onChange={(html) => setDraft({ ...draft, case_study_html: html })} placeholder="Context, Approach, Results, What I'd change…" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} /> Featured
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="project-save"><Save className="h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FF({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={tid} />
    </div>
  );
}
function FT({ label, v, on, rows = 3 }) {
  return (
    <div>
      <Label className="label-xs">{label}</Label>
      <Textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-2" />
    </div>
  );
}
